/**
 * Auth Callback Page
 *
 * Handles email confirmation redirects from Supabase.
 * When users click the confirmation link in their email,
 * they're redirected here to complete the verification process.
 *
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { supabase } from '../lib/supabase'
import { consumeAuthReturnTo } from '../lib/authReturnTo'
import { useSEO } from '../hooks/useSEO'
import { SIGNUP_COMPLETED_KEY } from './SignUpSuccessPage'
import {
  normalizeNativeAuthErrorCategory,
  trackNativeAuthFailed,
  trackNativeSignupCompleted,
} from '../lib/analytics'
import { clearNativeAuthIntent, readNativeAuthIntent } from '../lib/nativeAuthIntent'
import {
  clearPendingSignupAttribution,
  flushPendingSignupAttribution,
  isNewSignupAccount,
  markPendingSignupAccount,
  prepareSignupAttribution,
} from '../lib/signupAttribution'
import { getPostAuthDestination } from '../lib/postAuthRouting'

export default function AuthCallbackPage() {
  useSEO('/auth/callback')
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Please wait...')

  useEffect(() => {
    const handleCallback = async () => {
      const nativeAuthIntent = readNativeAuthIntent()

      try {
        // Check URL parameters for OAuth errors or cancellation
        const params = new URLSearchParams(window.location.search)
        const errorParam = params.get('error')
        const errorDescription = params.get('error_description')

        // Handle OAuth cancellation or errors
        if (errorParam === 'access_denied' || errorDescription?.includes('denied')) {
          // console.log('OAuth cancelled by user')
          sessionStorage.removeItem('optioneer_signup_intent')
          if (nativeAuthIntent) {
            trackNativeAuthFailed(
              nativeAuthIntent.platform,
              nativeAuthIntent.provider,
              'cancelled',
              nativeAuthIntent.entrySection
            )
            clearNativeAuthIntent()
          }
          setStatus('error')
          setMessage('Sign-in cancelled. Redirecting back...')
          setTimeout(() => navigate('/signin'), 2000)
          return
        }

        if (errorParam) {
          console.error('OAuth error:', errorParam, errorDescription)
          sessionStorage.removeItem('optioneer_signup_intent')
          if (nativeAuthIntent) {
            trackNativeAuthFailed(
              nativeAuthIntent.platform,
              nativeAuthIntent.provider,
              normalizeNativeAuthErrorCategory(errorDescription || errorParam),
              nativeAuthIntent.entrySection
            )
            clearNativeAuthIntent()
          }
          setStatus('error')
          setMessage('Authentication failed. Please try again.')
          setTimeout(() => navigate('/signin?error=auth_failed'), 2000)
          return
        }

        // Get the current session after email confirmation or OAuth
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession()

        if (error) {
          console.error('Auth callback error:', error)
          if (nativeAuthIntent) {
            trackNativeAuthFailed(
              nativeAuthIntent.platform,
              nativeAuthIntent.provider,
              normalizeNativeAuthErrorCategory(error),
              nativeAuthIntent.entrySection
            )
            clearNativeAuthIntent()
          }
          setStatus('error')
          setMessage('Authentication failed. Please try again or contact support.')
          setTimeout(() => navigate('/signin?error=confirmation_failed'), 3000)
          return
        }

        if (session) {
          // OAuth intent is a button action. The authenticated account must
          // actually have been created by this signup request before it completes.
          sessionStorage.removeItem('optioneer_signup_intent')
          const user = session.user
          const trackNativeCompletion = () => {
            if (nativeAuthIntent?.mode === 'signup') {
              trackNativeSignupCompleted(
                nativeAuthIntent.platform,
                nativeAuthIntent.provider,
                nativeAuthIntent.entrySection
              )
              clearNativeAuthIntent()
            }
          }
          const isNewSignup = isNewSignupAccount(user)
          if (isNewSignup) {
            if (!markPendingSignupAccount(user.id, user.created_at)) {
              // Google sign-in can create a brand-new account without using the
              // signup form. Creation/first-session evidence allows this one flow.
              await prepareSignupAttribution(user.created_at)
              markPendingSignupAccount(user.id, user.created_at)
            }
            await flushPendingSignupAttribution()
          } else {
            clearPendingSignupAttribution()
          }

          const authReturnTo = consumeAuthReturnTo()
          if (authReturnTo) {
            if (isNewSignup) {
              trackNativeCompletion()
              sessionStorage.setItem(SIGNUP_COMPLETED_KEY, 'true')
              setStatus('success')
              setMessage('Finishing your account setup...')
              setTimeout(
                () => navigate('/signup/success', { state: { redirectTo: authReturnTo } }),
                800
              )
              return
            }

            if (nativeAuthIntent) {
              clearNativeAuthIntent()
            }
            setStatus('success')
            setMessage('Redirecting you back...')
            setTimeout(() => navigate(authReturnTo), 800)
            return
          }

          if (isNewSignup) {
            trackNativeCompletion()
            // Mark signup as completed only for the success-page routing guard.
            sessionStorage.setItem(SIGNUP_COMPLETED_KEY, 'true')

            setStatus('success')
            setMessage('Account created! Welcome to Optioneer...')
            setTimeout(() => navigate('/signup/success'), 800)
            return
          }

          // Resolve native iOS subscription access before opening the dashboard.
          if (nativeAuthIntent) {
            clearNativeAuthIntent()
          }
          const destination = getPostAuthDestination()
          setStatus('success')
          setMessage(
            destination === '/dashboard'
              ? 'Redirecting to dashboard...'
              : 'Checking your Premium access...'
          )
          setTimeout(() => navigate(destination, { replace: true }), 800)
        } else {
          // No session but no error - email confirmed but user needs to sign in
          if (nativeAuthIntent) {
            clearNativeAuthIntent()
          }
          setStatus('success')
          setMessage('Email confirmed! Please sign in to continue.')
          setTimeout(() => navigate('/signin?message=email_confirmed'), 2000)
        }
      } catch (err) {
        console.error('Unexpected error during callback:', err)
        if (nativeAuthIntent) {
          trackNativeAuthFailed(
            nativeAuthIntent.platform,
            nativeAuthIntent.provider,
            normalizeNativeAuthErrorCategory(err),
            nativeAuthIntent.entrySection
          )
          clearNativeAuthIntent()
        }
        setStatus('error')
        setMessage('Something went wrong. Please try signing in again.')
        setTimeout(() => navigate('/signin'), 3000)
      }
    }

    handleCallback()
  }, [navigate])

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md space-y-4 text-center">
        {/* Loading/Success/Error Icon */}
        {status === 'loading' && (
          <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-gray-200 dark:border-slate-700 border-t-brand-600 dark:border-t-amber-500"></div>
        )}

        {status === 'success' && (
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <svg
              className="h-10 w-10 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}

        {status === 'error' && (
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <svg
              className="h-10 w-10 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        )}

        {/* Status Message */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {status === 'loading'
              ? 'Confirming Email'
              : status === 'success'
                ? 'Success!'
                : 'Confirmation Failed'}
          </h2>
          <p className="mt-2 text-gray-600 dark:text-slate-400">{message}</p>
        </div>

        {/* Manual navigation if auto-redirect doesn't work */}
        {status !== 'loading' && (
          <div className="mt-6">
            <button
              onClick={() => navigate(status === 'success' ? '/dashboard' : '/signin')}
              className="rounded-md bg-brand-600 px-6 py-2 text-white hover:bg-brand-700 transition-colors"
            >
              {status === 'success' ? 'Go to Dashboard' : 'Back to Sign In'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
